import React from "react";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import {
  daysAgoIso,
  formatHktDate,
  formatHktDateTime,
  hktBoundToUtc,
  hktDayEndUtc,
  hktDayStartUtc,
  isHktMidnight,
} from "@shared/lib/datetime";
import type { AuditFilters as Filters } from "@shared/api/audit";
import DayPicker from "@staff/components/DayPicker";

// F-E Phase 4b — filter bar. Date pickers are HKT calendar days converted to a
// UTC range for the API (wire is UTC; presentation is HKT). Other filters map
// straight to the API's snake_case query params via the audit.ts wrapper.
// F-AC: the pickers show YYYY-MM-DD whatever the browser's locale, and a
// rolling preset (24h / 7d / 30d) says exactly where it starts — its From box
// alone would read as midnight.

interface Props {
  filters: Filters;
  onChange: (partial: Partial<Filters>) => void;
  onReset: () => void;
}

// UTC ISO → HKT "YYYY-MM-DD" for a picker value.
function isoToHktDay(iso: string | undefined): string {
  return iso ? formatHktDate(iso) : "";
}

const fieldCls = "min-w-[8rem]";

const AuditFilters: React.FC<Props> = ({ filters, onChange, onReset }) => {
  // A shared link may carry a bare day or an offset-less time (review #5).
  const from = filters.from && hktBoundToUtc(filters.from);
  const to = filters.to && hktBoundToUtc(filters.to, true);

  // Presets are open-ended (2026-07-24): setting `to` would freeze the
  // range and hide events that happen after the click.
  const setPreset = (days: number) =>
    onChange({ from: daysAgoIso(days), to: undefined });

  return (
    <div className="bg-white border border-border rounded-lg p-3 flex flex-wrap items-end gap-3">
      <div className="flex gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPreset(1)}
          data-testid="audit-preset-24h"
        >
          24h
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPreset(7)}
          data-testid="audit-preset-7d"
        >
          7d
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPreset(30)}
          data-testid="audit-preset-30d"
        >
          30d
        </Button>
      </div>

      <div className="text-xs text-muted-foreground">
        <label className="block">From (HKT)</label>
        <DayPicker
          value={isoToHktDay(from)}
          onChange={(day) =>
            onChange({ from: day ? hktDayStartUtc(day) : undefined })
          }
          ariaLabel="From"
          testId="audit-filter-from"
          allowEmpty
        />
      </div>
      <div className="text-xs text-muted-foreground">
        <label className="block">To (HKT)</label>
        <DayPicker
          value={isoToHktDay(to)}
          onChange={(day) =>
            onChange({ to: day ? hktDayEndUtc(day) : undefined })
          }
          ariaLabel="To"
          testId="audit-filter-to"
          allowEmpty
        />
      </div>

      <label className="text-xs text-muted-foreground">
        Actor
        <Input
          className={fieldCls}
          placeholder="username or usr_…"
          value={filters.actorId ?? ""}
          onChange={(e) => onChange({ actorId: e.target.value || undefined })}
          data-testid="audit-filter-actor"
        />
      </label>
      <label className="text-xs text-muted-foreground">
        Action
        <Input
          className={fieldCls}
          placeholder="e.g. create, publish, auth:login"
          value={filters.action ?? ""}
          onChange={(e) => onChange({ action: e.target.value || undefined })}
          data-testid="audit-filter-action"
        />
      </label>
      <label className="text-xs text-muted-foreground">
        Resource
        <Input
          className={fieldCls}
          placeholder="title / name or exh_…"
          value={filters.resourceId ?? ""}
          onChange={(e) =>
            onChange({ resourceId: e.target.value || undefined })
          }
          data-testid="audit-filter-resource"
        />
      </label>
      <label className="text-xs text-muted-foreground">
        Decision
        <select
          className="block h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={filters.decision ?? ""}
          onChange={(e) =>
            onChange({
              decision: (e.target.value || undefined) as Filters["decision"],
            })
          }
          data-testid="audit-filter-decision"
        >
          <option value="">All</option>
          <option value="allow">Allow ✓</option>
          <option value="deny">Deny ✗</option>
        </select>
      </label>

      <Button
        variant="ghost"
        size="sm"
        onClick={onReset}
        data-testid="audit-filter-reset"
      >
        Reset
      </Button>
      {from && !isHktMidnight(from) && (
        <p
          className="basis-full text-xs text-muted-foreground"
          data-testid="audit-filter-since"
        >
          Showing events since {formatHktDateTime(from)} (HKT)
        </p>
      )}
    </div>
  );
};

export default AuditFilters;
